import React, { useState, useEffect } from 'react';
import ChatContainer from './components/ChatContainer';
import Header from './components/Header';
import Admin from './components/Admin';
import Login from './components/Login';
import { chatService } from './services/chatService';
import './App.css';

function App() {
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId] = useState('default');
  const [isConnected, setIsConnected] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [isBltzShieldEnabled, setIsBltzShieldEnabled] = useState(false);
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Initialize authentication state from localStorage after component mounts
  useEffect(() => {
    try {
      const savedUser = localStorage.getItem('chatbot_user');
      const savedAuth = localStorage.getItem('chatbot_authenticated');
      
      if (savedUser && savedAuth === 'true') {
        setUser(JSON.parse(savedUser));
        setIsAuthenticated(true);
      }
    } catch (error) {
      console.error('Error loading authentication state:', error);
      // Clear corrupted data
      localStorage.removeItem('chatbot_user');
      localStorage.removeItem('chatbot_authenticated');
    }
  }, []);

  // Check server health on component mount
  useEffect(() => {
    const checkServerHealth = async () => {
      try {
        const response = await chatService.checkHealth();
        setIsConnected(response.status === 'Server is running');
      } catch (error) {
        console.error('Server health check failed:', error);
        setIsConnected(false);
      }
    };

    checkServerHealth();
  }, []);

  const handleSendMessage = async (messageText) => {
    if (!messageText.trim() || isLoading) return;

    // Add user message to chat
    const userMessage = {
      id: Date.now(),
      text: messageText,
      sender: 'user',
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    try {
      // Send message to backend with shield state
      const response = await chatService.sendMessage(messageText, conversationId, isBltzShieldEnabled);
      
      // Add bot response to chat
      const botMessage = {
        id: Date.now() + 1,
        text: response.response,
        sender: 'bot',
        timestamp: response.timestamp,
        securityWarning: response.securityWarning
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      
      // Add error message to chat
      const errorMessage = {
        id: Date.now() + 1,
        text: 'Sorry, I encountered an error processing your message. Please try again.',
        sender: 'bot',
        timestamp: new Date().toISOString(),
        isError: true
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearChat = async () => {
    try {
      await chatService.clearConversation(conversationId);
      setMessages([]);
    } catch (error) {
      console.error('Error clearing chat:', error);
    }
  };

  const handleToggleBltzShield = () => {
    const newState = !isBltzShieldEnabled;
    setIsBltzShieldEnabled(newState);
    // Shield state is now UI-only, backend will handle routing
  };

  const handleLogin = (userData) => {
    setUser(userData);
    setIsAuthenticated(true);
    localStorage.setItem('chatbot_user', JSON.stringify(userData));
    localStorage.setItem('chatbot_authenticated', 'true');
  };

  const handleLogout = () => {
    setUser(null);
    setIsAuthenticated(false);
    setMessages([]);
    localStorage.removeItem('chatbot_user');
    localStorage.removeItem('chatbot_authenticated');
  };

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <div className="app">
      <Header 
        isConnected={isConnected}
        onClearChat={handleClearChat}
        messageCount={messages.length}
        onShowAdmin={() => setShowAdmin(true)}
        isBltzShieldEnabled={isBltzShieldEnabled}
        onToggleBltzShield={handleToggleBltzShield}
        user={user}
        onLogout={handleLogout}
      />
      <ChatContainer
        messages={messages}
        onSendMessage={handleSendMessage}
        isLoading={isLoading}
        isConnected={isConnected}
        isBltzShieldEnabled={isBltzShieldEnabled}
      />
      <Admin 
        isVisible={showAdmin}
        onClose={() => setShowAdmin(false)}
      />
    </div>
  );
}

export default App;